import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import GlossaryText from "../components/GlossaryText";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, ArrowRight, LinkSimple, Sparkle, PaperPlaneTilt, Copy, ShieldCheck, Download, MagnifyingGlass, Paperclip, EyeSlash, Eye, ArrowCounterClockwise, Lightning, Gear, ChartLine } from "@phosphor-icons/react";
import SecurityTicketPanel from "../components/SecurityTicketPanel";
import FlowCustomFieldsDialog from "../components/FlowCustomFieldsDialog";
import FlowRulesDialog from "../components/FlowRulesDialog";
import FlowAnalytics from "../components/FlowAnalytics";

const STATUS_CLS = {
  cancelled: "bg-slate-100 text-slate-500 border-slate-300 line-through",
  open: "bg-secondary text-gray-700 border-gray-300",
  in_progress: "bg-blue-50 text-blue-700 border-blue-300",
  submitted: "bg-indigo-50 text-indigo-700 border-indigo-300",
  reviewed: "bg-cyan-50 text-cyan-700 border-cyan-300",
  approved: "bg-teal-50 text-teal-700 border-teal-300",
  mitigated: "bg-amber-50 text-amber-700 border-amber-300",
  completed: "bg-green-50 text-green-700 border-green-300",
  spot_check: "bg-purple-50 text-purple-700 border-purple-300",
  spot_check_reviewed: "bg-purple-50 text-purple-700 border-purple-300",
  closed: "bg-primary text-primary-foreground border-black",
  // CIP-007 R2 security lifecycle
  evaluating: "bg-indigo-50 text-indigo-700 border-indigo-300",
  no_patch_available: "bg-teal-50 text-teal-700 border-teal-300",
  patch_available: "bg-blue-50 text-blue-700 border-blue-300",
  patching: "bg-blue-100 text-blue-800 border-blue-400",
  mitigating: "bg-amber-50 text-amber-800 border-amber-300",
  mitigation_extension_requested: "bg-orange-50 text-orange-800 border-orange-300",
  mitigation_extension_approved: "bg-orange-100 text-orange-900 border-orange-400",
  patched: "bg-green-50 text-green-700 border-green-300",
  retired: "bg-slate-100 text-slate-700 border-slate-400",
};
const CATS = [["op", "O&P"], ["cip", "CIP"], ["security", "Security (Vulnerabilities)"]];

// Renders inputs for admin-defined custom fields, bound to a values object.
function CustomFieldInputs({ fields, values, onChange, testidPrefix = "flow-custom" }) {
  if (!fields?.length) return null;
  const set = (key, v) => onChange({ ...(values || {}), [key]: v });
  return (
    <div className="grid grid-cols-2 gap-3">
      {fields.map((f) => (
        <div key={f.key}>
          <Label className="text-xs">{f.label}</Label>
          {f.type === "select" ? (
            <Select value={(values || {})[f.key] || "none"} onValueChange={(v) => set(f.key, v === "none" ? "" : v)}>
              <SelectTrigger data-testid={`${testidPrefix}-${f.key}`} className="rounded-none mt-1"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input data-testid={`${testidPrefix}-${f.key}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
              value={(values || {})[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="rounded-none mt-1" />
          )}
        </div>
      ))}
    </div>
  );
}

// Comma-separated tags editor.
function TagsInput({ value = [], onChange, testid = "flow-tags-input" }) {
  return (
    <Input data-testid={testid} value={(value || []).join(", ")}
      onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
      className="rounded-none mt-1" placeholder="comma,separated,tags" />
  );
}

export default function ClaroFlow() {
  const { canEdit, isRole } = useAuth();
  const [view, setView] = useState("all");   // all | mine
  const [cat, setCat] = useState("op");
  const [tickets, setTickets] = useState([]);
  const [myWork, setMyWork] = useState({ tickets: [], total: 0, overdue: 0 });
  const [statuses, setStatuses] = useState([]);
  const [summary, setSummary] = useState({ op: {}, cip: {}, total: 0, overdue: 0 });
  const [users, setUsers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [trackerItems, setTrackerItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openClosed, setOpenClosed] = useState("open");   // open | closed | all  (default highlight Open)
  const [showSuppressed, setShowSuppressed] = useState(false);
  const [closedStatuses, setClosedStatuses] = useState(["closed", "completed", "spot_check_reviewed", "retired"]);
  const [customFields, setCustomFields] = useState([]);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewForm, setViewForm] = useState({ name: "", shared: false });
  const [selected, setSelected] = useState(new Set());
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTags, setBulkTags] = useState("");
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState("");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "op", substation_id: "", assignee: "", due_date: "", priority: "normal", description: "", tracker_item_id: "", tags: [], custom: {} });
  const [detail, setDetail] = useState(null);
  const [ev, setEv] = useState({ description: "", link: "", device_uid: "" });
  const [preChangeSnaps, setPreChangeSnaps] = useState([]);
  const [cr, setCr] = useState({ baseline_link_id: "", software_integrity_hash: "" });
  const [tmplOpen, setTmplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tmplForm, setTmplForm] = useState({ template_key: "", assignee: "", due_date: "", priority: "normal", substation_id: "", title_suffix: "" });

  const statusLabel = useMemo(() => Object.fromEntries(statuses), [statuses]);
  const catCounts = useMemo(() => {
    const c = {};
    CATS.forEach(([k]) => { c[k] = Object.values(summary[k] || {}).reduce((a, b) => a + b, 0); });
    return c;
  }, [summary]);

  const load = () => {
    const sup = showSuppressed ? "&suppressed=only" : "&suppressed=hide";
    api.get(`/flow/tickets?category=${cat}${sup}`).then((r) => {
      setTickets(r.data.tickets); setStatuses(r.data.statuses);
      if (r.data.closed_statuses) setClosedStatuses(r.data.closed_statuses);
    }).catch(() => {});
    api.get("/flow/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/flow/my-work").then((r) => { setMyWork(r.data); if (!statuses.length) setStatuses(r.data.statuses); }).catch(() => {});
  };
  useEffect(() => { load(); setSelected(new Set()); }, [cat, showSuppressed]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/prc005/substations").then((r) => setSubs(r.data.substations)).catch(() => {});
    api.get("/tracker").then((r) => setTrackerItems(r.data || [])).catch(() => {});
    api.get("/flow/custom-fields").then((r) => setCustomFields(r.data.custom_fields || [])).catch(() => {});
    loadViews();
  }, []);

  const loadViews = () => api.get("/flow/views").then((r) => setViews(r.data.views || [])).catch(() => {});
  const applyView = (v) => {
    const f = v.filters || {};
    if (f.category) setCat(f.category);
    setOpenClosed(f.open_closed || "open");
    setStatusFilter(f.status_filter || "all");
    setSearch(f.search || "");
    setShowSuppressed(!!f.suppressed);
    toast.success(`View “${v.name}” applied`);
  };
  const saveView = async () => {
    if (!viewForm.name.trim()) return toast.error("Name your view");
    try {
      await api.post("/flow/views", { name: viewForm.name, shared: viewForm.shared,
        filters: { category: cat, open_closed: openClosed, status_filter: statusFilter, search, suppressed: showSuppressed } });
      setSaveViewOpen(false); setViewForm({ name: "", shared: false }); loadViews();
      toast.success("View saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteView = async (vid, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this saved view?")) return;
    try { await api.delete(`/flow/views/${vid}`); loadViews(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const clearSelection = () => setSelected(new Set());
  const runBulk = async (action, value) => {
    const shownIds = new Set(shown.map((t) => t.id));
    const ids = Array.from(selected).filter((id) => shownIds.has(id));
    if (!ids.length) { clearSelection(); return; }
    try {
      const { data } = await api.post("/flow/tickets/bulk", { ids, action, value });
      toast.success(`${data.updated} updated${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      clearSelection(); setBulkTagOpen(false); setBulkAssignOpen(false); setBulkTags(""); setBulkAssignee(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const isClosed = (s) => closedStatuses.includes(s);
  const matchSearch = (t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = [t.title, t.description, t.assignee, ...(t.tags || []),
      ...Object.values(t.custom || {})].join(" ").toLowerCase();
    return hay.includes(q);
  };
  const viewMatch = (s, v) => (v === "open" ? !isClosed(s) : v === "closed" ? isClosed(s) && s !== "cancelled" : v === "cancelled" ? s === "cancelled" : true);
  const shown = tickets.filter((t) => {
    if (!viewMatch(t.status, openClosed)) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    return matchSearch(t);
  });

  const generate = async () => {
    try { const { data } = await api.post("/flow/generate"); load(); toast.success(`Generated ${data.created} ticket(s)`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createTicket = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      await api.post("/flow/tickets", { ...form, due_date: form.due_date || null, tracker_item_id: form.tracker_item_id || null });
      setOpen(false);
      setForm({ title: "", category: cat, substation_id: "", assignee: "", due_date: "", priority: "normal", description: "", tracker_item_id: "", tags: [], custom: {} });
      load();
      toast.success("Ticket created");
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openTemplates = async () => {
    try {
      const { data } = await api.get("/flow/templates");
      const list = [...(data.builtin || []), ...(data.custom || [])];
      setTemplates(list);
      setTmplForm({ template_key: list[0]?.key || "", assignee: "", due_date: "", priority: "normal", substation_id: "", title_suffix: "" });
      setTmplOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createFromTemplate = async () => {
    if (!tmplForm.template_key) return toast.error("Pick a template");
    try {
      await api.post("/flow/tickets/from-template", { ...tmplForm, due_date: tmplForm.due_date || null, substation_id: tmplForm.substation_id || null });
      setTmplOpen(false); setCat("cip"); load(); toast.success("CIP workflow ticket created from template");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDetail = (t) => {
    setDetail(t);
    setEv({ description: "", link: "", device_uid: "" });
    setCr({ baseline_link_id: t.baseline_link_id || "", software_integrity_hash: t.software_integrity_hash || "" });
    if (t.category === "cip") api.get("/cip010/baselines").then((r) => setPreChangeSnaps((r.data.snapshots || []).filter((s) => s.snapshot_type === "pre_change"))).catch(() => {});
  };
  const saveCr = async () => {
    try {
      await api.put(`/flow/tickets/${detail.id}`, { ...detail, baseline_link_id: cr.baseline_link_id || null, software_integrity_hash: cr.software_integrity_hash, due_date: detail.due_date || null });
      refreshDetail(detail.id); toast.success("Change-management details saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const refreshDetail = async (tid) => { const { data } = await api.get(`/flow/tickets/${tid}`); setDetail(data); load(); };
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const tid = p.get("ticket");
    if (p.get("category")) setCat(p.get("category"));
    if (tid) api.get(`/flow/tickets/${tid}`).then(({ data }) => openDetail(data)).catch(() => toast.error("Ticket not found in this entity."));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const setStatus = async (status) => {
    try { await api.post(`/flow/tickets/${detail.id}/status`, { status }); refreshDetail(detail.id); toast.success(`Moved to ${statusLabel[status] || status}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addEvidence = async () => {
    if (!ev.description.trim()) return toast.error("Evidence description required");
    try { await api.post(`/flow/tickets/${detail.id}/evidence`, ev); setEv({ description: "", link: "", device_uid: "" }); refreshDetail(detail.id); toast.success("Evidence added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeEvidence = async (eid) => { await api.delete(`/flow/tickets/${detail.id}/evidence/${eid}`); refreshDetail(detail.id); };
  const toggleItem = async (idx) => {
    const items = detail.test_items.map((it, i) => i === idx ? { ...it, done: !it.done } : it);
    await api.put(`/flow/tickets/${detail.id}`, { ...detail, test_items: items, due_date: detail.due_date || null });
    refreshDetail(detail.id);
  };
  const removeTicket = async (tid) => { if (!window.confirm("Delete this ticket?")) return; await api.delete(`/flow/tickets/${tid}`); setDetail(null); load(); toast.success("Ticket deleted"); };
  const suppressTicket = async (suppressed) => {
    try { await api.post(`/flow/tickets/${detail.id}/suppress`, { suppressed }); setDetail(null); load(); toast.success(suppressed ? "Ticket suppressed" : "Ticket unsuppressed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancelTicket = async () => {
    const reason = window.prompt("Reason for cancelling this ticket (required):", "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required to cancel a ticket."); return; }
    try { await api.post(`/flow/tickets/${detail.id}/cancel`, { reason }); refreshDetail(detail.id); load(); toast.success("Ticket cancelled"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reopenTicket = async () => {
    const reason = window.prompt("Reason for reopening (optional):", "") ?? "";
    try { await api.post(`/flow/tickets/${detail.id}/reopen`, { reason }); refreshDetail(detail.id); load(); toast.success("Ticket reopened"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveMeta = async () => {
    try { await api.put(`/flow/tickets/${detail.id}`, { ...detail, due_date: detail.due_date || null }); refreshDetail(detail.id); toast.success("Ticket details saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/flow/tickets/${detail.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refreshDetail(detail.id); toast.success("File attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };
  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/flow/tickets/${detail.id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = att.filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };
  const deleteAttachment = async (aid) => { await api.delete(`/flow/tickets/${detail.id}/attachments/${aid}`); refreshDetail(detail.id); };
  const startTicket = async (tid, e) => {
    e?.stopPropagation();
    try { await api.post(`/flow/tickets/${tid}/status`, { status: "in_progress" }); load(); toast.success("Ticket started"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sendDigest = async () => {
    try { const { data } = await api.post("/flow/digest/send"); toast.success(`Digest: ${data.recipients} recipient(s), ${data.emailed} emailed`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const generateSecCycle = async () => {
    const ed = window.prompt("Evaluation date (YYYY-MM-DD) for this CIP-007 R2 cycle:", new Date().toISOString().slice(0, 10));
    if (!ed) return;
    try {
      const { data } = await api.post("/flow/security/generate-eval-cycle", { evaluation_date: ed, priority: "normal" });
      toast.success(`Cycle ${ed}: ${data.created} new ticket(s), ${data.skipped} skipped. Action due ${data.action_due}. Next eval ${data.next_eval_due}.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runSecReminders = async () => {
    try { const { data } = await api.post("/flow/security/reminders"); toast.success(`Reminders: ${data.reminded} overdue ticket(s) flagged / ${data.checked} checked`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportSecAudit = async () => {
    try {
      const res = await api.get("/flow/security/export-audit", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cip007_r2_audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CIP-007 R2 audit export ready");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };

  const nextStatuses = () => {
    if (!detail || isClosed(detail.status)) return [];
    const idx = statuses.findIndex(([k]) => k === detail.status);
    const end = statuses.findIndex(([k]) => k === "closed");
    return statuses.slice(idx + 1, end + 1).map(([k]) => k);
  };
  const today = new Date().toISOString().slice(0, 10);
  const sc = summary[cat] || {};

  return (
    <div>
      <PageHeader title="ClaroFlow" subtitle="Work tickets & maintenance workflows — route, track and evidence compliance tasks"
        action={canEdit && <div className="flex gap-2">
          {isRole("admin") && <Button data-testid="flow-digest-btn" variant="outline" onClick={sendDigest} className="rounded-none"><PaperPlaneTilt size={16} className="mr-1" />Send Digest</Button>}
          {cat === "security" && <Button data-testid="flow-sec-gen-cycle" variant="outline" onClick={generateSecCycle} className="rounded-none"><ShieldCheck size={16} className="mr-1" />New R2 Eval Cycle</Button>}
          {cat === "security" && <Button data-testid="flow-sec-reminders" variant="outline" onClick={runSecReminders} className="rounded-none"><Sparkle size={16} className="mr-1" />Run Overdue Reminders</Button>}
          {cat === "security" && <Button data-testid="flow-sec-export-audit" variant="outline" onClick={exportSecAudit} className="rounded-none"><Download size={16} className="mr-1" />Export R2 Audit</Button>}
          <Button data-testid="flow-generate-btn" variant="outline" onClick={generate} className="rounded-none"><Sparkle size={16} className="mr-1" />Generate PRC-005 Tickets</Button>
          <Button data-testid="flow-template-btn" variant="outline" onClick={openTemplates} className="rounded-none"><Copy size={16} className="mr-1" />CIP Templates</Button>
          {isRole("admin") && <Button data-testid="flow-fields-btn" variant="outline" onClick={() => setFieldsOpen(true)} className="rounded-none"><Gear size={16} className="mr-1" />Custom Fields</Button>}
          {isRole("admin") && <Button data-testid="flow-automation-btn" variant="outline" onClick={() => setRulesOpen(true)} className="rounded-none"><Lightning size={16} className="mr-1" />Automation</Button>}
          <Button data-testid="flow-new-btn" onClick={() => { setForm((f) => ({ ...f, category: cat, tags: [], custom: {} })); setOpen(true); }} className="rounded-none bg-primary hover:bg-primary/90"><Plus size={16} className="mr-1" />New Ticket</Button>
        </div>} />

      <div className="p-8 space-y-5">
        {/* view toggle: My Work vs All Tickets */}
        <div className="flex gap-2" data-testid="flow-view-toggle">
          <button data-testid="flow-view-mine" onClick={() => setView("mine")} className={`border px-4 py-2 text-sm font-semibold rounded-none ${view === "mine" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
            My Work ({myWork.total}{myWork.overdue ? <span className="text-red-300"> · {myWork.overdue} overdue</span> : ""})
          </button>
          <button data-testid="flow-view-all" onClick={() => setView("all")} className={`border px-4 py-2 text-sm font-semibold rounded-none ${view === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>All Tickets</button>
          <button data-testid="flow-view-analytics" onClick={() => setView("analytics")} className={`border px-4 py-2 text-sm font-semibold rounded-none inline-flex items-center gap-1.5 ${view === "analytics" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}><ChartLine size={15} />Analytics</button>
        </div>

        {view === "analytics" ? <FlowAnalytics /> : view === "mine" ? (
          <div className="space-y-2" data-testid="flow-mywork-list">
            {myWork.tickets.length === 0 ? <Empty title="You're all caught up" hint="No open tickets are assigned to you right now." /> :
              myWork.tickets.map((t) => (
                <div key={t.id} data-testid={`mywork-ticket-${t.id}`} onClick={() => openDetail(t)} className={`border bg-card p-4 cursor-pointer hover:border-primary transition-colors ${t.overdue ? "border-red-300" : "border-border"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-display font-bold truncate">{t.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{t.test_items?.length || 0} test item{(t.test_items?.length || 0) === 1 ? "" : "s"} · due <span className={t.overdue ? "text-red-600 font-semibold" : ""}>{t.due_date || "TBD"}</span>{t.overdue && <span className="ml-2 border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5 text-[10px] font-bold uppercase">Overdue</span>}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STATUS_CLS[t.status] || ""}`}>{statusLabel[t.status] || t.status}</span>
                      {canEdit && t.status === "open" && <Button data-testid={`mywork-start-${t.id}`} size="sm" onClick={(e) => startTicket(t.id, e)} className="rounded-none bg-primary hover:bg-primary/90">Start</Button>}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (<>
        {/* category tabs */}
        <div className="flex gap-2" data-testid="flow-category-tabs">
          {CATS.map(([k, l]) => (
            <button key={k} data-testid={`flow-cat-${k}`} onClick={() => { setCat(k); setStatusFilter("all"); }}
              className={`border px-4 py-2 text-sm font-semibold rounded-none ${cat === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
              {l} ({catCounts[k] || 0})
            </button>
          ))}
        </div>

        {/* saved views */}
        <div className="flex flex-wrap items-center gap-2" data-testid="flow-saved-views">
          <span className="overline text-muted-foreground mr-1">Saved views:</span>
          {views.length === 0 && <span className="text-xs text-muted-foreground italic">none yet</span>}
          {views.map((v) => (
            <span key={v.id} data-testid={`flow-view-${v.id}`} onClick={() => applyView(v)}
              className="group inline-flex items-center gap-1 border border-border bg-card hover:border-primary px-2.5 py-1 text-xs font-semibold rounded-none cursor-pointer">
              {v.name}
              {v.shared && <span className="text-[9px] font-normal text-muted-foreground uppercase">shared</span>}
              {!v.shared && <span className="text-[9px] font-normal text-muted-foreground uppercase">only me</span>}
              {(v.is_owner || isRole("admin")) && <button data-testid={`flow-view-del-${v.id}`} onClick={(e) => deleteView(v.id, e)} className="ml-0.5 text-muted-foreground hover:text-red-600"><Trash size={11} /></button>}
            </span>
          ))}
          {canEdit && <button data-testid="flow-save-view-btn" onClick={() => setSaveViewOpen(true)} className="inline-flex items-center gap-1 border border-dashed border-border hover:bg-secondary px-2.5 py-1 text-xs font-semibold rounded-none"><Plus size={12} />Save current view</button>}
        </div>

        {/* summary chips */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="flow-summary">
          {[["Open", sc.open || 0, "bg-card"], ["In Progress", sc.in_progress || 0, "bg-blue-50 border-blue-300 text-blue-700"],
            ["Completed", sc.completed || 0, "bg-green-50 border-green-300 text-green-700"],
            ["Overdue", summary.overdue || 0, "bg-red-50 border-red-300 text-red-700"]].map(([l, v, cls]) => (
            <div key={l} className={`border border-border p-4 ${cls}`}><div className="font-display font-black text-3xl tabular-nums">{v}</div><div className="overline text-muted-foreground">{l}</div></div>
          ))}
        </div>

        {/* search + open/closed + suppressed toolbar */}
        <div className="flex flex-wrap items-center gap-3" data-testid="flow-toolbar">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="flow-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, description, assignee, tags, custom fields…" className="rounded-none pl-8" />
          </div>
          <div className="flex gap-1" data-testid="flow-openclosed-toggle">
            {[["open", "Open"], ["closed", "Closed"], ["cancelled", "Cancelled"], ["all", "All"]].map(([k, l]) => (
              <button key={k} data-testid={`flow-oc-${k}`} onClick={() => { setOpenClosed(k); setStatusFilter("all"); }}
                className={`border px-3 py-1.5 text-xs font-semibold rounded-none ${openClosed === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{l} ({tickets.filter((t) => viewMatch(t.status, k)).length})</button>
            ))}
          </div>
          <button data-testid="flow-export-xlsx" onClick={() => downloadBlob(`/flow/export/tickets.xlsx?view=${openClosed}&category=${cat}`, `claroflow_${cat}_${openClosed}.xlsx`)}
            className="border border-border hover:bg-secondary px-3 py-1.5 text-xs font-semibold rounded-none inline-flex items-center gap-1.5"><Download size={14} />Export Excel</button>
          <button data-testid="flow-suppressed-toggle" onClick={() => { setShowSuppressed((v) => !v); setStatusFilter("all"); }}
            className={`border px-3 py-1.5 text-xs font-semibold rounded-none inline-flex items-center gap-1.5 ${showSuppressed ? "bg-slate-700 text-white border-slate-800" : "border-border hover:bg-secondary"}`}
            title="Show suppressed (muted) tickets">
            {showSuppressed ? <Eye size={14} /> : <EyeSlash size={14} />}{showSuppressed ? "Suppressed view" : "Show suppressed"}
          </button>
        </div>

        {/* status filter */}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setStatusFilter("all")} className={`border px-2.5 py-1 text-xs font-semibold rounded-none ${statusFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>All ({tickets.length})</button>
          {statuses.map(([k, l]) => { const n = tickets.filter((t) => t.status === k).length; if (!n) return null; return (
            <button key={k} data-testid={`flow-filter-${k}`} onClick={() => setStatusFilter(k)} className={`border px-2.5 py-1 text-xs font-semibold rounded-none ${statusFilter === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{l} ({n})</button>
          ); })}
        </div>

        {selected.size > 0 && (
          <div className="sticky top-2 z-10 flex flex-wrap items-center gap-2 border border-primary bg-primary/10 px-3 py-2" data-testid="flow-bulk-bar">
            <span className="text-xs font-semibold">{selected.size} selected</span>
            <Button data-testid="bulk-suppress" size="sm" variant="outline" className="rounded-none" onClick={() => runBulk("suppress")}><EyeSlash size={13} className="mr-1" />Suppress</Button>
            <Button data-testid="bulk-unsuppress" size="sm" variant="outline" className="rounded-none" onClick={() => runBulk("unsuppress")}><Eye size={13} className="mr-1" />Unsuppress</Button>
            <Button data-testid="bulk-reopen" size="sm" variant="outline" className="rounded-none" onClick={() => runBulk("reopen")}><ArrowCounterClockwise size={13} className="mr-1" />Reopen</Button>
            <Button data-testid="bulk-tag" size="sm" variant="outline" className="rounded-none" onClick={() => setBulkTagOpen(true)}><Plus size={13} className="mr-1" />Add tags</Button>
            <Button data-testid="bulk-reassign" size="sm" variant="outline" className="rounded-none" onClick={() => setBulkAssignOpen(true)}>Reassign</Button>
            <button data-testid="bulk-clear" onClick={clearSelection} className="text-xs text-muted-foreground hover:text-foreground ml-auto">Clear</button>
          </div>
        )}

        {shown.length === 0 ? <Empty title="No tickets" hint="Generate PRC-005 tickets from device due dates, or create one manually." /> : (
          <div className="space-y-2">
            {shown.map((t) => {
              const overdue = !["completed", "closed"].includes(t.status) && (t.due_date || "9999") < today;
              return (
                <div key={t.id} data-testid={`flow-ticket-${t.id}`} onClick={() => openDetail(t)} className={`border bg-card p-4 cursor-pointer hover:border-primary transition-colors ${selected.has(t.id) ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      {canEdit && <span onClick={(e) => e.stopPropagation()} className="mt-0.5"><Checkbox data-testid={`flow-select-${t.id}`} checked={selected.has(t.id)} onCheckedChange={() => toggleSelect(t.id)} className="rounded-none" /></span>}
                      <div className="min-w-0">
                      <div className="font-display font-bold truncate">{t.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {t.test_items?.length || 0} test item{(t.test_items?.length || 0) === 1 ? "" : "s"} · {t.assignee || "Unassigned"} {t.due_date && <>· due <span className={overdue ? "text-red-600 font-semibold" : ""}>{t.due_date}</span></>}
                      </div>
                      {((t.tags || []).length > 0 || t.suppressed) && (
                        <div className="flex flex-wrap items-center gap-1 mt-1.5">
                          {t.suppressed && <span className="inline-flex items-center gap-1 border border-slate-400 bg-slate-100 dark:bg-slate-800 dark:border-slate-600 px-1.5 py-0.5 text-[10px] font-semibold"><EyeSlash size={9} />Suppressed</span>}
                          {(t.tags || []).map((tg) => <span key={tg} className="border border-border bg-secondary/50 px-1.5 py-0.5 text-[10px] font-medium">{tg}</span>)}
                        </div>
                      )}
                      </div>
                    </div>
                    <span className={`shrink-0 border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STATUS_CLS[t.status] || ""}`}>{statusLabel[t.status] || t.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>)}
      </div>

      {/* New ticket dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle>New Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="flow-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "normal", "high"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Assignee (SME)</Label>
                <Select value={form.assignee || "none"} onValueChange={(v) => setForm({ ...form, assignee: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="flow-assignee-select" className="rounded-none mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div><Label>Substation (optional)</Label>
              <Select value={form.substation_id || "none"} onValueChange={(v) => setForm({ ...form, substation_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-none mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Link to recurring tracker item (optional)</Label>
              <p className="text-[11px] text-muted-foreground mb-1">Marking this ticket <span className="font-semibold">Completed</span> will auto-log the completion on the linked CIPWatch/OPWatch calendar and roll its next-due date forward.</p>
              <Select value={form.tracker_item_id || "none"} onValueChange={(v) => setForm({ ...form, tracker_item_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="flow-tracker-link-select" className="rounded-none mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {trackerItems
                    .filter((it) => (form.category === "cip" ? it.track === "cip" : it.track === "op"))
                    .map((it) => (
                      <SelectItem key={it.id} value={it.id}>
                        {it.standard_id} {it.req_number}{it.subpart ? `.${it.subpart}` : ""} — {(it.description || "").slice(0, 50)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Tags</Label><TagsInput value={form.tags} onChange={(tags) => setForm({ ...form, tags })} /></div>
            {customFields.length > 0 && <CustomFieldInputs fields={customFields} values={form.custom} onChange={(custom) => setForm({ ...form, custom })} testidPrefix="flow-new-custom" />}
            <Button data-testid="flow-save-btn" onClick={createTicket} className="rounded-none w-full bg-primary hover:bg-primary/90">Create Ticket</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CIP workflow template dialog */}
      <Dialog open={tmplOpen} onOpenChange={setTmplOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="flow-template-dialog">
          <DialogHeader><DialogTitle>New CIP Workflow from Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Workflow template</Label>
              <Select value={tmplForm.template_key} onValueChange={(v) => setTmplForm({ ...tmplForm, template_key: v })}>
                <SelectTrigger data-testid="flow-template-select" className="rounded-none mt-1"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.key} value={t.key}>{t.title}{t.builtin === false ? " (custom)" : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(() => {
              const sel = templates.find((t) => t.key === tmplForm.template_key);
              if (!sel) return null;
              return (
                <div data-testid="flow-template-preview" className="border border-border bg-secondary/30 p-3 text-xs space-y-1">
                  <p className="text-muted-foreground">{sel.description}</p>
                  <div className="font-semibold text-foreground mt-1">{(sel.test_items || []).length} checklist step(s):</div>
                  <ul className="list-disc pl-4 space-y-0.5">{(sel.test_items || []).map((it, i) => <li key={i}>{it.label}</li>)}</ul>
                </div>
              );
            })()}
            <div><Label>Title suffix (optional)</Label><Input data-testid="flow-template-suffix" value={tmplForm.title_suffix} onChange={(e) => setTmplForm({ ...tmplForm, title_suffix: e.target.value })} placeholder="e.g. SCADA firmware update Q3" className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Assignee (SME)</Label>
                <Select value={tmplForm.assignee || "none"} onValueChange={(v) => setTmplForm({ ...tmplForm, assignee: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="flow-template-assignee" className="rounded-none mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Due date</Label><Input type="date" value={tmplForm.due_date} onChange={(e) => setTmplForm({ ...tmplForm, due_date: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <Button data-testid="flow-template-create-btn" onClick={createFromTemplate} className="rounded-none w-full bg-primary hover:bg-primary/90">Create CIP Ticket</Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && <>
            <DialogHeader><DialogTitle className="pr-8">{detail.title}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className={`border px-2 py-0.5 text-[11px] font-semibold uppercase ${STATUS_CLS[detail.status] || ""}`}>{statusLabel[detail.status] || detail.status}</span>
                {detail.status === "cancelled" && detail.cancel_reason && (
                  <span className="text-xs text-muted-foreground" data-testid="flow-cancel-reason">Cancelled by {detail.cancelled_by}: {detail.cancel_reason}</span>
                )}
                <span className="text-muted-foreground">{detail.assignee || "Unassigned"}</span>
                {detail.due_date && <span className="text-muted-foreground">· due {detail.due_date}</span>}
                <span className="text-muted-foreground">· {detail.priority}</span>
              </div>
              {detail.description && <p className="text-sm text-muted-foreground"><GlossaryText text={detail.description} /></p>}

              {detail.suppressed && (
                <div className="border border-slate-400 bg-slate-100 dark:bg-slate-800 dark:border-slate-600 px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="flow-detail-suppressed">
                  <EyeSlash size={13} />This ticket is suppressed (muted from active views).
                </div>
              )}

              {/* tags + custom attributes */}
              {canEdit && (
                <div className="border border-border p-3 space-y-3" data-testid="flow-detail-meta">
                  <div><Label className="text-xs">Tags</Label><TagsInput value={detail.tags || []} onChange={(tags) => setDetail({ ...detail, tags })} testid="flow-detail-tags" /></div>
                  {customFields.length > 0 && <CustomFieldInputs fields={customFields} values={detail.custom} onChange={(custom) => setDetail({ ...detail, custom })} testidPrefix="flow-detail-custom" />}
                  <Button data-testid="flow-meta-save" size="sm" variant="outline" className="rounded-none" onClick={saveMeta}>Save tags &amp; fields</Button>
                </div>
              )}
              {!canEdit && (detail.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1">{(detail.tags || []).map((tg) => <span key={tg} className="border border-border bg-secondary/50 px-1.5 py-0.5 text-[10px] font-medium">{tg}</span>)}</div>
              )}

              {/* status transitions (OP/CIP only — security has its own state machine below) */}
              {detail.category !== "security" && isRole("admin", "analyst", "reviewer") && nextStatuses().length > 0 && (
                <div>
                  <div className="overline text-muted-foreground mb-1">Advance status</div>
                  <div className="flex flex-wrap gap-2" data-testid="flow-status-actions">
                    {nextStatuses().slice(0, 3).map((k) => (
                      <Button key={k} data-testid={`flow-status-${k}`} size="sm" variant="outline" className="rounded-none" onClick={() => setStatus(k)}><ArrowRight size={13} className="mr-1" />{statusLabel[k] || k}</Button>
                    ))}
                  </div>
                </div>
              )}

              {/* CIP-007 R2 patch-management panel — security tickets only */}
              {detail.category === "security" && (
                <SecurityTicketPanel ticket={detail} onChange={() => refreshDetail(detail.id)} />
              )}

              {/* CIP-010 Change Management (Pre-Deployment Gate) */}
              {detail.category === "cip" && isRole("admin", "analyst", "reviewer") && (
                <div className="border border-amber-300 bg-amber-50/50 p-3" data-testid="flow-cip010-cr">
                  <div className="overline text-muted-foreground mb-2">CIP-010 Change Management — Pre-Deployment Gate</div>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Pre-Change Baseline Snapshot (required to Deploy)</Label>
                      <select className="w-full border border-border rounded px-2 py-1.5 text-sm mt-1" value={cr.baseline_link_id} onChange={(e) => setCr({ ...cr, baseline_link_id: e.target.value })} data-testid="flow-baseline-select">
                        <option value="">— none linked —</option>
                        {preChangeSnaps.map((s) => <option key={s.id} value={s.id}>{s.asset_name} · {(s.snapshot_timestamp || "").slice(0, 10)} · {s.sha256_hash.slice(0, 8)}…{s.verified ? "" : " (TAMPERED)"}</option>)}
                      </select>
                      {cr.baseline_link_id && !preChangeSnaps.find((s) => s.id === cr.baseline_link_id) && <div className="text-[11px] text-muted-foreground mt-1">Linked snapshot id: {cr.baseline_link_id}</div>}
                    </div>
                    <div>
                      <Label className="text-xs">Software Integrity Verification (SHA-256 of patch, required to Deploy)</Label>
                      <Input className="rounded-none mt-1 font-mono text-xs" value={cr.software_integrity_hash} onChange={(e) => setCr({ ...cr, software_integrity_hash: e.target.value })} data-testid="flow-swhash-input" placeholder="Vendor-provided SHA-256 of the patch/software" />
                    </div>
                    <Button size="sm" variant="outline" className="rounded-none" onClick={saveCr} data-testid="flow-cr-save"><LinkSimple size={13} className="mr-1" />Save change-management details</Button>
                  </div>
                </div>
              )}

              {/* test items */}
              {detail.test_items?.length > 0 && (
                <div>
                  <div className="overline text-muted-foreground mb-1">Required tests</div>
                  <div className="border border-border divide-y divide-border">
                    {detail.test_items.map((it, i) => (
                      <div key={`${it.activity_code || "ti"}-${it.device_uid || ""}-${i}`} className="p-2.5 flex items-start gap-2 text-sm">
                        <Checkbox data-testid={`flow-item-${i}`} checked={!!it.done} onCheckedChange={() => toggleItem(i)} className="rounded-none mt-0.5" />
                        <div className="min-w-0">
                          <div className="font-semibold">{it.label} {it.activity_code && <span className="font-mono text-xs text-muted-foreground">[{it.activity_code}]</span>}</div>
                          {it.activities && <div className="text-xs text-muted-foreground whitespace-pre-line">{it.activities}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* evidence */}
              <div>
                <div className="overline text-muted-foreground mb-1">Evidence <span className="normal-case font-normal">(provide a description + external link; store files outside the program where possible)</span></div>
                {(detail.evidence || []).length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {detail.evidence.map((e) => (
                      <div key={e.id} className="border border-border p-2 text-sm flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium">{e.description}</div>
                          {e.link && <a href={e.link} target="_blank" rel="noreferrer" className="text-xs text-blue-600 inline-flex items-center gap-1 truncate"><LinkSimple size={12} />{e.link}</a>}
                          {e.device_uid && <span className="text-[11px] text-muted-foreground ml-2">{e.device_uid}</span>}
                        </div>
                        {canEdit && <button onClick={() => removeEvidence(e.id)} className="text-muted-foreground hover:text-red-600 shrink-0"><Trash size={14} /></button>}
                      </div>
                    ))}
                  </div>
                )}
                {canEdit && (
                  <div className="border border-border p-3 space-y-2">
                    <Input data-testid="flow-evidence-desc" placeholder="What does this evidence show?" value={ev.description} onChange={(e) => setEv({ ...ev, description: e.target.value })} className="rounded-none" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid="flow-evidence-link" placeholder="External link (preferred)" value={ev.link} onChange={(e) => setEv({ ...ev, link: e.target.value })} className="rounded-none" />
                      <Input placeholder="Device ID (optional)" value={ev.device_uid} onChange={(e) => setEv({ ...ev, device_uid: e.target.value })} className="rounded-none" />
                    </div>
                    <Button data-testid="flow-add-evidence-btn" size="sm" onClick={addEvidence} className="rounded-none bg-primary hover:bg-primary/90">Add evidence</Button>
                  </div>
                )}
              </div>

              {/* file attachments */}
              <div data-testid="flow-attachments">
                <div className="overline text-muted-foreground mb-1">Attachments <span className="normal-case font-normal">(documents, images & media stored securely)</span></div>
                {(detail.attachments || []).length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {detail.attachments.map((a) => (
                      <div key={a.id} data-testid={`flow-attachment-${a.id}`} className="border border-border p-2 text-sm flex items-center justify-between gap-2">
                        <button onClick={() => downloadAttachment(a)} className="min-w-0 inline-flex items-center gap-2 text-blue-600 hover:underline truncate">
                          <Paperclip size={14} className="shrink-0" /><span className="truncate">{a.filename}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{Math.max(1, Math.round((a.size || 0) / 1024))} KB</span>
                        </button>
                        {canEdit && <button data-testid={`flow-attachment-del-${a.id}`} onClick={() => deleteAttachment(a.id)} className="text-muted-foreground hover:text-red-600 shrink-0"><Trash size={14} /></button>}
                      </div>
                    ))}
                  </div>
                )}
                {canEdit && (
                  <label className="inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-xs cursor-pointer hover:bg-secondary rounded-none" data-testid="flow-attach-upload">
                    <Paperclip size={14} />Attach file
                    <input type="file" className="hidden" onChange={uploadAttachment} data-testid="flow-attach-input" />
                  </label>
                )}
              </div>

              {(detail.history || []).length > 0 && (
                <div data-testid="flow-history">
                  <div className="overline text-muted-foreground mb-1">History</div>
                  <div className="border border-border divide-y divide-border max-h-48 overflow-y-auto">
                    {[...detail.history].reverse().map((h, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <span className="font-semibold uppercase tracking-wide">{statusLabel[h.status] || h.status}</span>
                          {h.note && <span className="text-muted-foreground"> — {h.note}</span>}
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0">{h.by || "—"} · {(h.at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {canEdit && (
                <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
                  {isClosed(detail.status) && (
                    <Button data-testid="flow-reopen-btn" size="sm" variant="outline" className="rounded-none" onClick={reopenTicket}><ArrowCounterClockwise size={13} className="mr-1" />Reopen</Button>
                  )}
                  {!isClosed(detail.status) && (
                    <Button data-testid="flow-cancel-btn" size="sm" variant="outline" className="rounded-none" onClick={cancelTicket}>Cancel ticket</Button>
                  )}
                  {detail.suppressed
                    ? <button data-testid="flow-unsuppress-btn" onClick={() => suppressTicket(false)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Eye size={13} />Unsuppress</button>
                    : <button data-testid="flow-suppress-btn" onClick={() => suppressTicket(true)} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><EyeSlash size={13} />Suppress</button>}
                  <button data-testid="flow-delete-btn" onClick={() => removeTicket(detail.id)} className="text-xs text-muted-foreground hover:text-red-600 inline-flex items-center gap-1 ml-auto"><Trash size={13} />Delete ticket</button>
                </div>
              )}
            </div>
          </>}
        </DialogContent>
      </Dialog>
      <FlowCustomFieldsDialog open={fieldsOpen} onOpenChange={setFieldsOpen} onSaved={() => api.get("/flow/custom-fields").then((r) => setCustomFields(r.data.custom_fields || []))} />
      <FlowRulesDialog open={rulesOpen} onOpenChange={setRulesOpen} users={users} customFields={customFields} statuses={statuses} />

      {/* Save current view dialog */}
      <Dialog open={saveViewOpen} onOpenChange={setSaveViewOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="flow-save-view-dialog">
          <DialogHeader><DialogTitle>Save current view</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Saves the current category, Open/Closed, status, search and suppressed filters as a one-click view pinned to the dashboard.</p>
            <div><Label>View name</Label><Input data-testid="flow-view-name" value={viewForm.name} onChange={(e) => setViewForm({ ...viewForm, name: e.target.value })} className="rounded-none mt-1" placeholder="e.g. High-priority open CIP" /></div>
            <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="flow-view-shared" checked={viewForm.shared} onCheckedChange={(v) => setViewForm({ ...viewForm, shared: !!v })} className="rounded-none" />Share with everyone in this entity</label>
            <Button data-testid="flow-view-save" onClick={saveView} className="rounded-none w-full bg-primary hover:bg-primary/90">Save view</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk add tags dialog */}
      <Dialog open={bulkTagOpen} onOpenChange={setBulkTagOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="flow-bulk-tag-dialog">
          <DialogHeader><DialogTitle>Add tags to {selected.size} ticket(s)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tags (comma-separated)</Label><Input data-testid="bulk-tag-input" value={bulkTags} onChange={(e) => setBulkTags(e.target.value)} className="rounded-none mt-1" placeholder="q3, audit" /></div>
            <Button data-testid="bulk-tag-apply" onClick={() => runBulk("add_tags", bulkTags.split(",").map((s) => s.trim()).filter(Boolean))} className="rounded-none w-full bg-primary hover:bg-primary/90">Add tags</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk reassign dialog */}
      <Dialog open={bulkAssignOpen} onOpenChange={setBulkAssignOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="flow-bulk-assign-dialog">
          <DialogHeader><DialogTitle>Reassign {selected.size} ticket(s)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Assignee</Label>
              <Select value={bulkAssignee || "none"} onValueChange={(v) => setBulkAssignee(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="bulk-assign-select" className="rounded-none mt-1"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="bulk-assign-apply" onClick={() => runBulk("reassign", bulkAssignee)} className="rounded-none w-full bg-primary hover:bg-primary/90">Reassign</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
